/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 * Copyright 2021 Adobe
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/

import { DOC_FOLDER_EMFS } from "../filesystem/TQFile";
import TQFont, { tqtypes, TQFontAggregation, TQFontFamily } from "../../public/TQTypes";
import TQTaskScheduler, { downloaderVersion } from "../../controller/TQTaskScheduler";
import { CacheResponseType } from "../cache/TQResponseStorageCache";

export default class TQFoundFontHandler {
    private _scheduler: TQTaskScheduler;
    private _fontRetryCount = 3;

    constructor(scheduler: TQTaskScheduler) {
        this._scheduler = scheduler;
    }

    private _getFontKeyForCache(font: TQFont, forceCache: boolean) {
        let fontKey: any = undefined;
        if (font.isVariableFont) {
            fontKey = CacheResponseType.VARIABLE_FONT_ENDPOINT_PREFIX + font.variableFamilyPrefix + downloaderVersion + CacheResponseType.VARIABLE_FONT_ENDPOINT_SUFFIX;
        } else if (font.type == tqtypes.FontType.BYOF) {
            fontKey = CacheResponseType.BYOF_FONT_ENDPOINT_PREFIX + font.fontId + downloaderVersion + CacheResponseType.BYOF_FONT_ENDPOINT_SUFFIX;
        } else if (font.type == tqtypes.FontType.APPLICATION) {
            fontKey = CacheResponseType.APP_FONT_ENDPOINT_PREFIX + font.fontId + downloaderVersion + CacheResponseType.APP_FONT_ENDPOINT_SUFFIX;
        } else if (forceCache) {
            if (font.postscriptName != "") {
                fontKey = CacheResponseType.DEFAULT_FONT_ENDPOINT_PREFIX + font.postscriptName + downloaderVersion + CacheResponseType.DEFAULT_FONT_ENDPOINT_SUFFIX;
            } else if (font.fontId != "") {
                fontKey = CacheResponseType.DEFAULT_FONT_ENDPOINT_PREFIX + font.fontId + downloaderVersion + CacheResponseType.DEFAULT_FONT_ENDPOINT_SUFFIX;
            }
        }
        return fontKey;
    }

    public async populateFontBufferFromCache(font: TQFont, forceCache: boolean) {
        if (this._scheduler.initParams.diskCache == tqtypes.DiskCacheType.OPFS) {
            const fontStream = await this._scheduler.getFontFromDisk(font.postscriptName);
            if (fontStream != undefined) {
                font.fontBuffer = fontStream;
                font.filePath = DOC_FOLDER_EMFS + font.postscriptName;
                return true;
            }
        }

        if (forceCache || font.isVariableFont || font.type == tqtypes.FontType.BYOF || font.type == tqtypes.FontType.APPLICATION) {
            const fontStream = await this.populateFontIfCached(font, forceCache);
            if (fontStream) {
                font.fontBuffer = new Uint8Array(await fontStream.arrayBuffer());
                this._scheduler.populateCharacterSet(font);
                if (font.type == tqtypes.FontType.BYOF) {
                    await this._scheduler.populateByofScript(font);
                }
                return true;
            }
        }
    }

    public async populateFontIfCached(font: TQFont, forceCache: boolean) {
        const fontKey = this._getFontKeyForCache(font, forceCache);
        if (fontKey) {
            return await this._scheduler.responseCache.getBlobValue(fontKey);
        } else {
            return undefined;
        }
    }

    public async putFontBufferInCache(font: TQFont, forceCache: boolean) {
        if (this._scheduler.initParams.diskCache == tqtypes.DiskCacheType.OPFS) {
            const success = await TQTaskScheduler._diskCache.addFile(font.postscriptName, font.postscriptName, font.fontBuffer.length, font.fontBuffer);
            if (success) {
                font.filePath = DOC_FOLDER_EMFS + font.postscriptName;
            }
        }

        if (forceCache || font.isVariableFont || font.type == tqtypes.FontType.BYOF || font.type == tqtypes.FontType.APPLICATION) {
            const fontKey = this._getFontKeyForCache(font, forceCache);
            if (fontKey) {
                await this._scheduler.responseCache.putBlobValue(fontKey, font.fontBuffer);
            }
        }
    }

    /**
     * API call to get TQFont (with only metadata) for the given postscript names of Fonts.
     * @param {string[]} psNames Postscript names list
     * @param {string[]} fontIds Typekit ids list
     * @return {Promise<TQFont[]>} Promise for the array of created TQFont(s) as a result. If a font was not found, it's typekitid would be empty
     */
    public async checkFonts(psNames: string[], fontIds: string[], entitlement: tqtypes.Entitlement | undefined) {
        const psNamesMap = new Map<string, TQFont>();
        const tkIdsMap = new Map<string, TQFont>();

        const remainingPsNames: string[] = [];
        const remainingTkIds: string[] = [];

        for (const psName of psNames) {
            psNamesMap.set(psName, new TQFont(tqtypes.FontType.DEFAULT, psName, ""));
        }
        for (const fontId of fontIds) {
            tkIdsMap.set(fontId, new TQFont(tqtypes.FontType.DEFAULT, "", fontId));
        }

        psNames = psNames.filter(entry => entry != "");
        fontIds = fontIds.filter(entry => entry != "");

        for (const entry of psNames) {
            const fontIfExists = await this._scheduler.getFontFromLocalInfo(entry);
            if (fontIfExists == undefined || fontIfExists.fontId == "") {
                remainingPsNames.push(entry); // not found, find it.
            } else {
                psNamesMap.set(entry, fontIfExists);
            }
        }
        for (const entry of fontIds) {
            const fontIfExists = await this._scheduler.getFontFromLocalInfo("", entry);
            if (fontIfExists == undefined || fontIfExists.postscriptName == "") {
                remainingTkIds.push(entry); // not found, find it.
            } else {
                tkIdsMap.set(entry, fontIfExists);
            }
        }

        const perChunk = 30; // max 30 items per chunk
        const psNamesArrayOfChunks = remainingPsNames.reduce((resultArray: string[][], item: any, index: any) => {
            const chunkIndex = Math.floor(index / perChunk);
            if (!resultArray[chunkIndex]) {
                resultArray[chunkIndex] = []; // start a new chunk
            }
            resultArray[chunkIndex].push(item);
            return resultArray;
        }, []);

        const tkIdArrayOfChunks = remainingTkIds.reduce((resultArray: string[][], item: any, index: any) => {
            const chunkIndex = Math.floor(index / perChunk);
            if (!resultArray[chunkIndex]) {
                resultArray[chunkIndex] = []; // start a new chunk
            }
            resultArray[chunkIndex].push(item);
            return resultArray;
        }, []);

        // follow the regular path for rest of the fonts with a max batch of 30
        if (entitlement == tqtypes.Entitlement.ANONYMOUS) {
            return { psNames: psNamesMap, fontIds: tkIdsMap }; // no go for service search
        }
        let tkIdIndex = 0;
        for (let index = 0; index < psNamesArrayOfChunks.length; index++) {
            const callbacks: tqtypes.GenericContext<tqtypes.InternalContext> = {
                root: {
                    downloadCB: null as any,
                    fontsList: psNamesArrayOfChunks[index],
                    fontsIds: tkIdIndex < tkIdArrayOfChunks.length ? tkIdArrayOfChunks[tkIdIndex] : [],
                    unicodesList: [],
                    fontInfo: new TQFont(tqtypes.FontType.DEFAULT),
                },
                retryCount: this._fontRetryCount,
            };
            const retFonts: TQFont[] = await this._scheduler.scheduleTask(tqtypes.TaskType.FONT_SEARCH, callbacks);
            for (const font of retFonts) {
                if (psNamesArrayOfChunks[index].indexOf(font.postscriptName) != -1) {
                    psNamesMap.set(font.postscriptName, font);
                }
                if (tkIdIndex < tkIdArrayOfChunks.length && tkIdArrayOfChunks[tkIdIndex].indexOf(font.fontId) != -1) {
                    tkIdsMap.set(font.fontId, font);
                }
            }
            tkIdIndex++;
        }

        for (; tkIdIndex < tkIdArrayOfChunks.length; tkIdIndex++) {
            const callbacks: tqtypes.GenericContext<tqtypes.InternalContext> = {
                root: {
                    downloadCB: null as any,
                    fontsList: [],
                    fontsIds: tkIdArrayOfChunks[tkIdIndex],
                    unicodesList: [],
                    fontInfo: new TQFont(tqtypes.FontType.DEFAULT),
                },
                retryCount: this._fontRetryCount,
            };
            const retFonts: TQFont[] = await this._scheduler.scheduleTask(tqtypes.TaskType.FONT_SEARCH, callbacks);
            for (const font of retFonts) {
                if (tkIdArrayOfChunks[tkIdIndex].indexOf(font.fontId) != -1) {
                    tkIdsMap.set(font.fontId, font);
                }
            }
        }
        return { psNames: psNamesMap, fontIds: tkIdsMap };
    }
}
